import { useCallback } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import type { Todo, TodoStatus, TodoPriority } from '../api/types'
import { useUpdateTodo, useDeleteTodo } from '../api/hooks'
import { useToast } from '../components/primitives/Toast'
import { apiFetch } from '../api/client'
import { idempotencyKey } from '../lib/ulid'
import { useNoteDrawerStore } from '../state/noteDrawer'
import { useUIStore } from '../store/ui'
import { computeSnappedPosition, sortTodos, STATUS_RANK } from '../lib/todoSort'
import { atEnd } from '../lib/fracidx'
import { formatDate, todayStr } from '../lib/parse'

const STATUS_ORDER: TodoStatus[] = ['todo', 'done']

function cycleStatus(current: TodoStatus): TodoStatus {
  const idx = STATUS_ORDER.indexOf(current)
  return STATUS_ORDER[(idx + 1) % STATUS_ORDER.length]
}

export interface TodoActions {
  toggleStatus(): void
  saveEditedText(next: string): void
  deleteImmediate(): void
  deleteWithUndo(): void
  openNote(): void
  setPriority(p: TodoPriority | null): void
  setReminder(at: string | null): void
  moveToDate(dateStr: string): void
  moveToSection(sectionId: string): void
  moveToNextDay(): void
}

export function useTodoActions(todo: Todo): TodoActions {
  const updateTodo = useUpdateTodo()
  const deleteTodo = useDeleteTodo()
  const { showToast } = useToast()
  const qc = useQueryClient()
  const smartStatusSort = useUIStore((s) => s.smartStatusSort)

  // Flatten every cached ['todos', …] list into one array. Used wherever an
  // action needs to reason about a todo's siblings across columns/sections.
  const snapshotAllTodos = useCallback((): Todo[] => {
    const allEntries = qc.getQueriesData<Todo[]>({ queryKey: ['todos'] })
    const all: Todo[] = []
    for (const [, data] of allEntries) {
      if (data) all.push(...data)
    }
    return all
  }, [qc])

  // Fractional position that drops this todo at the end of whichever
  // destination column (date or section) matches `inDestination`.
  const endPositionOf = useCallback(
    (inDestination: (t: Todo) => boolean): number => {
      const dest = snapshotAllTodos().filter((t) => t.id !== todo.id && inDestination(t))
      const maxPos = dest.reduce((m, t) => Math.max(m, t.position), 0)
      return atEnd(maxPos)
    },
    [snapshotAllTodos, todo.id]
  )

  const toggleStatus = useCallback(() => {
    const next = cycleStatus(todo.status)

    if (!smartStatusSort) {
      updateTodo.mutate({ id: todo.id, status: next })
      return
    }

    const allTodos = snapshotAllTodos()
    const siblings = allTodos.filter(
      (t) =>
        t.id !== todo.id &&
        (todo.date !== null ? t.date === todo.date : t.section_id === todo.section_id)
    )

    const sortedSiblings = sortTodos(siblings, true)
    const destRank = STATUS_RANK[next]
    const firstOfGroup = sortedSiblings.find((t) => STATUS_RANK[t.status] === destRank)
    const beforeId = firstOfGroup?.id ?? null
    const newPosition = computeSnappedPosition(sortedSiblings, next, beforeId)
    updateTodo.mutate({ id: todo.id, status: next, position: newPosition })
  }, [todo.id, todo.status, todo.date, todo.section_id, updateTodo, smartStatusSort, snapshotAllTodos])

  const saveEditedText = useCallback(
    (next: string) => {
      const trimmed = next.trim()
      if (!trimmed) {
        deleteTodo.mutate(todo.id)
        return
      }
      if (trimmed !== todo.text) {
        updateTodo.mutate({ id: todo.id, text: trimmed })
      }
    },
    [todo.id, todo.text, updateTodo, deleteTodo]
  )

  const deleteImmediate = useCallback(() => {
    deleteTodo.mutate(todo.id, {
      onError: () => showToast('Failed to delete todo', 'error'),
    })
  }, [todo.id, deleteTodo, showToast])

  const deleteWithUndo = useCallback(() => {
    const snapshot = { ...todo }
    const UNDO_MS = 4000

    // Snapshot every affected query so Undo can restore the exact prior cache
    // shape (positions, ordering across columns, etc.).
    const prevQueries = qc.getQueriesData<Todo[]>({ queryKey: ['todos'] })
    qc.setQueriesData<Todo[]>({ queryKey: ['todos'] }, (old) =>
      old?.filter((t) => t.id !== snapshot.id)
    )

    // Fire the real soft-delete now so the server is consistent with the UI.
    // Undo awaits this promise before issuing POST /restore to avoid a race
    // where restore arrives before the soft-delete commits (which would 404).
    const deletePromise = apiFetch<void>(`/todos/${snapshot.id}`, {
      method: 'DELETE',
      idempotencyKey: idempotencyKey(),
    })

    showToast(`Deleted "${snapshot.text}"`, 'info', {
      duration: UNDO_MS,
      action: {
        label: 'Undo',
        onClick: async () => {
          try {
            await deletePromise
            await apiFetch<Todo>(`/todos/${snapshot.id}/restore`, {
              method: 'POST',
              idempotencyKey: idempotencyKey(),
            })
            for (const [queryKey, data] of prevQueries) {
              qc.setQueryData(queryKey, data)
            }
          } catch {
            showToast('Failed to restore todo', 'error')
          }
        },
      },
    })

    deletePromise.catch(() => {
      for (const [queryKey, data] of prevQueries) {
        qc.setQueryData(queryKey, data)
      }
      showToast('Failed to delete todo', 'error')
    })
  }, [todo, qc, showToast])

  const openNote = useCallback(() => {
    useNoteDrawerStore.getState().open(todo.id)
  }, [todo.id])

  const setPriority = useCallback(
    (p: TodoPriority | null) => {
      updateTodo.mutate({ id: todo.id, priority: p ?? '' })
    },
    [todo.id, updateTodo]
  )

  const setReminder = useCallback(
    (at: string | null) => {
      updateTodo.mutate({ id: todo.id, reminder_at: at })
    },
    [todo.id, updateTodo]
  )

  // Move onto a day (week view). The `''` clears section_id (the API treats an
  // empty string as "set to null"), detaching a section todo so it surfaces in
  // the week instead of staying pinned to its section.
  const moveToDate = useCallback(
    (dateStr: string) => {
      const position = endPositionOf((t) => t.date === dateStr)
      updateTodo.mutate({ id: todo.id, date: dateStr, section_id: '', position })
    },
    [todo.id, updateTodo, endPositionOf]
  )

  // Move into a section. The `''` clears the date, detaching a week todo from
  // its day (same empty-string-clears convention as above).
  const moveToSection = useCallback(
    (sectionId: string) => {
      const position = endPositionOf((t) => t.section_id === sectionId)
      updateTodo.mutate({ id: todo.id, section_id: sectionId, date: '', position })
    },
    [todo.id, updateTodo, endPositionOf]
  )

  const moveToNextDay = useCallback(() => {
    if (todo.date === null) {
      moveToDate(todayStr())
    } else {
      const d = new Date(todo.date + 'T00:00:00')
      d.setDate(d.getDate() + 1)
      moveToDate(formatDate(d))
    }
  }, [todo.date, moveToDate])

  return {
    toggleStatus,
    saveEditedText,
    deleteImmediate,
    deleteWithUndo,
    openNote,
    setPriority,
    setReminder,
    moveToDate,
    moveToSection,
    moveToNextDay,
  }
}
